import { Button, Form, Image, Input, Modal, Radio, Select, Upload, message } from "antd";
import type { UploadFile, UploadProps } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import type { CampaignMeetingTypeConfigForm, CampaignRegistrationForm, CampaignRegistrationVo, Company, Page } from "@zhihaoscm/entity";
import { CAMPAIGN_REGISTRATION_STATE, GIFT_DURATION_UNIT, ORDER_PAY_TYPE, PARTICIPATION } from "@zhihaoscm/meta";
import { formatCurrency } from "@zhihaoscm/utils";
import {
    campaignRegistrationCompanyHeadSelector,
    campaignRegistrationPay,
    campaignRegistrationUpdate,
    campaignRegistrationUpdatePayFile
} from "@/api/campaignRegistration";
import { fileService } from "@/api/fileService";
import { paymentService } from "@/api/paymentService";
import { QrCodeDialog } from "@/components/media";
import { validateFile } from "@/components/media/validation";
import { resolveNotificationHtml } from "@/pages/userMessage/NotificationDetailPage";

export interface RegistrationDialogProps {
    open: boolean;
    data: CampaignRegistrationVo;
    onClose: () => void;
    onSuccess: () => void;
}

/** 将 Vue 参会配置中的字段数组转换为表单字段字典。 */
function fieldConfigMap(data: CampaignRegistrationVo, meetingType?: number) {
    const config = data.meetingTypeConfig?.find(item => item.type === meetingType);
    return {
        config,
        fields: Object.fromEntries((config?.fieldConfig ?? []).filter(item => item.fieldName).map(item => [item.fieldName!, item]))
    };
}

/** 与 Vue memberText 计算属性一致，显示参会权益赠送说明。 */
function memberGiftText(data: CampaignRegistrationVo, meetingType?: number) {
    // 后端 VO 实际会返回报名配置表单的赠送会员字段，实体旧声明尚未同步这些可选字段。
    const config = data.meetingTypeConfig?.find(item => item.type === meetingType) as CampaignMeetingTypeConfigForm | undefined;
    if (!config?.isGiveMember) return "";
    const member = config.giveMemberType === 1 ? "基础会员" : config.giveMemberType === 2 ? "高级会员" : "";
    const duration =
        config.giveDurationUnit === GIFT_DURATION_UNIT.DAY.code
            ? `赠送${config.giveDuration}天`
            : config.giveDurationUnit === GIFT_DURATION_UNIT.MONTH.code
              ? `赠送${config.giveDuration}个月`
              : "";
    return `${duration}${member}*${config.giveCount}`;
}

/** 复刻旧 registrationAgreement：先解析历史媒体占位符，再显示协议正文。 */
function RegistrationAgreementDialog({ open, data, onCancel, onConfirm }: {
    open: boolean;
    data: CampaignRegistrationVo;
    onCancel: () => void;
    onConfirm: () => void;
}) {
    const [content, setContent] = useState("");
    useEffect(() => {
        if (open) void resolveNotificationHtml(data.campaign.protocolContent ?? "").then(setContent);
    }, [data.campaign.protocolContent, open]);
    return (
        <Modal
            className="registration-agreement-dialog"
            open={open}
            title={data.campaign.protocolTitle || "协议"}
            width={800}
            onCancel={onCancel}
            footer={<Button type="primary" onClick={onConfirm}>同意本协议</Button>}
        >
            <article className="registration-agreement" dangerouslySetInnerHTML={{ __html: content }} />
        </Modal>
    );
}

/** 修改报名信息，按 Vue 字段配置决定显隐、必填、人数上限和协议确认。 */
export function RegistrationEditDialog({ open, data, onClose, onSuccess }: RegistrationDialogProps) {
    const [form] = Form.useForm<CampaignRegistrationForm>();
    const [submitting, setSubmitting] = useState(false);
    const [agreementOpen, setAgreementOpen] = useState(false);
    const [agreementAccepted, setAgreementAccepted] = useState(false);
    const [pendingValues, setPendingValues] = useState<CampaignRegistrationForm>();
    const [companies, setCompanies] = useState<Company[]>([]);
    const [companyLoading, setCompanyLoading] = useState(false);
    const companyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const meetingType = Form.useWatch("meetingType", form) ?? data.campaignRegistration.meetingType;
    const { config, fields } = useMemo(() => fieldConfigMap(data, meetingType), [data, meetingType]);
    const showAttendees = Boolean(fields["职务"] || fields["姓名"] || fields["手机号"]);
    const regularParticipant = meetingType === PARTICIPATION.GENERAL_PARTICIPATION.code;
    const meetingTypeEditable = data.campaignRegistration.state === CAMPAIGN_REGISTRATION_STATE.WAIT_PAY.code;

    useEffect(() => {
        if (!open) return;
        form.setFieldsValue({
            id: data.campaignRegistration.id,
            campaignId: data.campaign.id,
            meetingType: data.campaignRegistration.meetingType,
            companyLetterhead: data.campaignRegistration.companyLetterhead,
            meetingRequirements: data.campaignRegistration.meetingRequirements,
            detailForms: data.campaignRegistrationApplicant.length
                ? data.campaignRegistrationApplicant.map(item => ({ id: String(item.id), position: item.position, name: item.name, mobile: item.mobile }))
                : [{}]
        });
        setAgreementAccepted(false);
    }, [data, form, open]);

    /** 远程查询企业抬头，沿用 Vue 1500ms 防抖，避免输入时高频请求。 */
    const searchCompany = (name: string) => {
        if (companyTimer.current) clearTimeout(companyTimer.current);
        if (!name) {
            setCompanies([]);
            return;
        }
        companyTimer.current = setTimeout(() => {
            setCompanyLoading(true);
            void campaignRegistrationCompanyHeadSelector({ page: 1, size: 50, name })
                .then(response => {
                    const page = response.data as unknown as Page<Company>;
                    setCompanies(page.list ?? []);
                })
                .finally(() => setCompanyLoading(false));
        }, 1500);
    };

    /** 协议确认与 Vue 一致：首次校验后弹协议，同意后继续提交同一份表单。 */
    const beginSubmit = async () => {
        const values = await form.validateFields();
        const primary = {
            ...values,
            detailForms: showAttendees ? (values.detailForms ?? []).filter(item => item.name || item.mobile || item.position) : []
        };
        if (data.campaign.needAgreeProtocol && !agreementAccepted) {
            setPendingValues(primary);
            setAgreementOpen(true);
            return;
        }
        await submit(primary);
    };
    const submit = async (values: CampaignRegistrationForm) => {
        setSubmitting(true);
        try {
            await campaignRegistrationUpdate(values);
            message.success("提交成功");
            onSuccess();
            onClose();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Modal className="registration-edit-dialog" open={open} title="修改报名信息" width={600}
                confirmLoading={submitting} okText="提交" cancelText="取消" onCancel={onClose}
                onOk={() => void beginSubmit()} destroyOnHidden>
                <Form form={form} className="registration-edit-form" labelCol={{ flex: "70px" }}>
                    {meetingTypeEditable ? (
                        <Form.Item name="meetingType" label="参会形式" required rules={[{ required: true, message: "请选择参会形式" }]}>
                            <Radio.Group options={(data.meetingTypeConfig ?? []).map(item => ({ label: PARTICIPATION.getIntroByCode(item.type), value: item.type }))}
                                onChange={() => form.setFieldsValue({ companyLetterhead: undefined, meetingRequirements: undefined, detailForms: [{}] })} />
                        </Form.Item>
                    ) : (
                        <div className="registration-edit-summary">
                            <div><span>参会形式</span><strong>{PARTICIPATION.getIntroByCode(meetingType)}</strong></div>
                            <div><span>报名费</span><strong className="registration-edit-fee"><small>¥</small>{formatCurrency(config?.registrationFee ?? 0)}{regularParticipant ? <small>/人</small> : null}</strong>{memberGiftText(data, meetingType) ? <em>{memberGiftText(data, meetingType)}</em> : null}</div>
                        </div>
                    )}
                    {meetingTypeEditable ? (
                        <Form.Item label="报名费"><strong className="registration-edit-fee"><small>¥</small>{formatCurrency(config?.registrationFee ?? 0)}{regularParticipant ? <small>/人</small> : null}</strong>{memberGiftText(data, meetingType) ? <em className="registration-member-gift">{memberGiftText(data, meetingType)}</em> : null}</Form.Item>
                    ) : null}
                    {fields["企业抬头"] ? (
                        <Form.Item name="companyLetterhead" label="企业抬头" required={Boolean(fields["企业抬头"].hasRequired)} rules={[{ required: Boolean(fields["企业抬头"].hasRequired), message: "请输入企业抬头" }]}>
                            <Select allowClear showSearch filterOption={false} loading={companyLoading} placeholder="请输入企业抬头" options={companies.map(item => ({ label: item.companyName, value: item.companyName }))} onSearch={searchCompany} />
                        </Form.Item>
                    ) : null}
                    {fields["参会需求"] ? (
                        <Form.Item name="meetingRequirements" label="参会需求" required={Boolean(fields["参会需求"].hasRequired)} rules={[{ required: Boolean(fields["参会需求"].hasRequired), message: "请输入参会需求" }]}>
                            <Input.TextArea rows={3} maxLength={200} showCount placeholder="请输入参会需求" />
                        </Form.Item>
                    ) : null}
                    {showAttendees ? (
                        <Form.List name="detailForms">
                            {(attendees, { add, remove }) => <>
                                <div className="registration-participant-heading"><span>报名信息</span>{!regularParticipant ? <small>当前权益支持添加{config?.maxParticipants}人参会</small> : null}</div>
                                <div className="registration-participant-list">
                                    {attendees.map((attendee, index) => <div className="registration-participant-card" key={attendee.key}>
                                        <div className="registration-participant-title"><strong>参会人员{index + 1}</strong>{attendees.length > 1 ? <button type="button" aria-label={`删除参会人员${index + 1}`} onClick={() => remove(attendee.name)}><i className="iconfont icon-delete1" /></button> : null}</div>
                                        {fields["职务"] ? <Form.Item name={[attendee.name, "position"]} label="职务" required={Boolean(fields["职务"].hasRequired)} rules={[{ required: Boolean(fields["职务"].hasRequired), message: "请输入职务" }, { max: 32, message: "不超过32个字符" }]}><Input allowClear placeholder="请输入职务" /></Form.Item> : null}
                                        {fields["姓名"] ? <Form.Item name={[attendee.name, "name"]} label="姓名" required={Boolean(fields["姓名"].hasRequired)} rules={[{ required: Boolean(fields["姓名"].hasRequired), message: "请输入姓名" }, { max: 32, message: "不超过32个字符" }]}><Input allowClear placeholder="请输入姓名" /></Form.Item> : null}
                                        {fields["手机号"] ? <Form.Item name={[attendee.name, "mobile"]} label="手机号" required={Boolean(fields["手机号"].hasRequired)} rules={[{ required: Boolean(fields["手机号"].hasRequired), message: "请输入手机号" }, { pattern: /^1[3456789]\d{9}$/, message: "请输入正确的手机号" }]}><Input allowClear maxLength={11} placeholder="请输入手机号" /></Form.Item> : null}
                                    </div>)}
                                </div>
                                {!regularParticipant && attendees.length < (config?.maxParticipants ?? 0) ? <Button className="registration-participant-add" type="link" onClick={() => add({})}><i className="iconfont icon-add" /> 增加</Button> : null}
                            </>}
                        </Form.List>
                    ) : null}
                </Form>
            </Modal>
            <RegistrationAgreementDialog open={agreementOpen} data={data} onCancel={() => setAgreementOpen(false)} onConfirm={() => { setAgreementAccepted(true); setAgreementOpen(false); if (pendingValues) void submit(pendingValues); }} />
        </>
    );
}

const acceptedVoucherTypes = [".zip", ".rar", ".7z", ".aip", "image/jpeg", "image/png", "image/webp", "image/avif", "application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"];

/** 线下提交前使用旧版同文案确认框。 */
function confirmVoucherSubmit() {
    return new Promise<boolean>(resolve => Modal.confirm({ title: "提示", content: "是否确认提交支付凭证", okText: "确认", cancelText: "取消", onOk: () => resolve(true), onCancel: () => resolve(false) }));
}

/** 报名支付完整映射旧版报名卡片、账户卡、收款码及凭证上传区。 */
export function RegistrationPaymentDialog({ open, data, onClose, onSuccess }: RegistrationDialogProps) {
    const registration = data.campaignRegistration;
    const editing = registration.state === CAMPAIGN_REGISTRATION_STATE.WAIT_CONFIRM_PAY.code;
    const [payType, setPayType] = useState(editing ? ORDER_PAY_TYPE.OFFLINE.code : ORDER_PAY_TYPE.ONLINE.code);
    const [files, setFiles] = useState<UploadFile[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [qrCode, setQrCode] = useState("");
    const [collectionQrCode, setCollectionQrCode] = useState("");

    useEffect(() => {
        if (!open) return;
        setPayType(editing ? ORDER_PAY_TYPE.OFFLINE.code : ORDER_PAY_TYPE.ONLINE.code);
        const voucherIds = registration.paymentVoucherFileIds ?? [];
        if (voucherIds.length) void fileService.getFileByIds(voucherIds).then(items => setFiles(items.map(item => ({ uid: String(item.id), name: item.name, status: "done", url: item.path, response: { id: item.id } }))));
        else setFiles([]);
        if (data.campaign.paymentQrCodeFileId) void fileService.getFileById(data.campaign.paymentQrCodeFileId).then(item => setCollectionQrCode(item.path));
        else setCollectionQrCode("");
    }, [data, editing, open, registration.paymentVoucherFileIds]);

    /** 每个凭证直接上传文件服务，response.id 作为支付接口的 fileIds。 */
    const request: UploadProps["customRequest"] = async ({ file, onSuccess, onError }) => {
        const raw = file as File;
        const error = validateFile(raw, { accept: acceptedVoucherTypes, maxSizeMb: 50 });
        if (error) { message.error(error); onError?.(new Error(error)); return; }
        const formData = new FormData(); formData.append("file", raw); formData.append("fileName", raw.name);
        try { const result = await fileService.uploadFile(formData); onSuccess?.(result); } catch (reason) { onError?.(reason as Error); }
    };

    const submit = async () => {
        if (payType === ORDER_PAY_TYPE.ONLINE.code) {
            setSubmitting(true);
            try {
                const payment = await paymentService.create({ payType: 1, businessType: 5, detail: { registrationCode: registration.code }, remark: "活动报名参会费", source: 1 });
                setQrCode(await paymentService.getQRCode({ payType: 1, outTradeNo: payment.outTradeNo, type: 1 }));
            } finally { setSubmitting(false); }
            return;
        }
        const ids = files.map(file => Number((file.response as { id?: number } | undefined)?.id ?? file.uid)).filter(Number.isFinite);
        if (!ids.length) { message.error("请上传支付凭证"); return; }
        if (!(await confirmVoucherSubmit())) return;
        setSubmitting(true);
        try {
            const payload = { id: registration.id, fileIds: ids };
            await (editing ? campaignRegistrationUpdatePayFile(payload) : campaignRegistrationPay(payload));
            message.success("提交成功"); onSuccess(); onClose();
        } finally { setSubmitting(false); }
    };

    return <>
        <Modal className="registration-payment-dialog" open={open} title={editing ? "修改支付凭证" : "支付报名费"} width={800}
            confirmLoading={submitting} okText="提交" cancelText="取消" onCancel={onClose} onOk={() => void submit()} destroyOnHidden>
            <div className="registration-payment-card">
                <div className="registration-payment-card-head"><div className="registration-payment-tag"><span>{PARTICIPATION.getIntroByCode(registration.meetingType)}</span>{data.meetingCount ? <><i /><span>{data.meetingCount}人</span></> : null}</div>{registration.companyLetterhead ? <strong>{registration.companyLetterhead}</strong> : null}</div>
                {data.campaignRegistrationApplicant.length ? <div className="registration-payment-attendees">{data.campaignRegistrationApplicant.map((item, index) => <div key={item.id}><span>{index + 1}、</span>{item.position ? <span>{item.position}</span> : null}{item.position && item.name ? <i /> : null}{item.name ? <span>{item.name}</span> : null}{(item.name || item.position) && item.mobile ? <i /> : null}{item.mobile ? <span>{item.mobile}</span> : null}</div>)}</div> : null}
                {data.campaignRegistrationApplicant.length && registration.meetingRequirements ? <div className="registration-payment-divider" /> : null}
                {registration.meetingRequirements ? <div className="registration-payment-requirements"><span>参会需求</span><p>{registration.meetingRequirements}</p></div> : null}
            </div>
            <div className="registration-payment-row"><span>报名费总计</span><strong><small>¥</small>{formatCurrency(registration.registrationFee || 0)}</strong></div>
            <div className="registration-payment-row"><span>支付方式</span>{editing ? <b>{ORDER_PAY_TYPE.getIntroByCode(payType)}</b> : <Radio.Group value={payType} options={ORDER_PAY_TYPE.options} onChange={event => setPayType(event.target.value)} />}</div>
            {payType === ORDER_PAY_TYPE.OFFLINE.code ? <>
                <div className="registration-payment-divider" />
                <h3 className="registration-payment-section-title">线下收款账号</h3>
                <div className="registration-bank-card">
                    {data.platformBankAccount?.account ? <div className="registration-bank-account"><strong>{data.platformBankAccount.account.replace(/.{4}(?=.)/g, "$& ")}</strong><Button type="link" onClick={() => void navigator.clipboard.writeText(data.platformBankAccount.account).then(() => message.success("已复制"))}>复制</Button></div> : null}
                    <div className="registration-bank-info"><div><span>开户名称</span><strong>{data.platformBankAccount?.name || "--"}</strong></div><div><span>开户行</span><strong>{data.platformBankAccount?.openingBank || "--"}</strong></div></div>
                </div>
                {collectionQrCode ? <><div className="registration-payment-divider" /><h3 className="registration-payment-section-title">收款二维码 <em>扫码付款时请备注开票企业抬头</em></h3><Image className="registration-collection-qrcode" src={collectionQrCode} width="30%" /></> : null}
                <div className="registration-payment-divider" />
                <div className="registration-voucher-upload"><label>上传支付凭证</label><Upload accept={acceptedVoucherTypes.join(",")} customRequest={request} fileList={files} maxCount={10} multiple onChange={({ fileList }) => setFiles(fileList)}><Button><i className="iconfont icon-upload" /> 上传文件</Button></Upload><small>支持最多上传10个文件，单个文件不超过50M（支持上传压缩包）</small></div>
            </> : null}
        </Modal>
        <QrCodeDialog open={Boolean(qrCode)} title="扫码支付" value={qrCode} onClose={() => { setQrCode(""); onSuccess(); onClose(); }} />
    </>;
}
